import { Box, Grid, HStack, Image, Stack, Table, Text } from "@chakra-ui/react";
import React from "react";
import AppointButton from "../components/custom/AppointButton";
import { FilledButton, SimpleOutlineButton } from "../components/custom/Button";
import RichText from "../components/custom/RichText";
import Steps from "../components/custom/Steps";
import { GiCheckMark } from "react-icons/gi";
import TestimonialSwiper from "../components/custom/TestimonialSwiper";
import { useColorModeValue } from "../components/ui/color-mode";
import { Helmet } from "react-helmet-async";

const BusinessScreen = () => {
  const secondBg = useColorModeValue("#b8fff7", "#090110");

  const tableData = [
    {
      business: "Private Limited Company",
      type: "Startups, SMEs",
      benefits: "High",
      compliance: "Moderate",
    },
    {
      business: "LLP (Limited Liability Partnership)",
      type: "Small Businesses, Consultants",
      benefits: "Medium",
      compliance: "Low",
    },
    {
      business: "One-Person Company (OPC)",
      type: "Solo Entrepreneurs",
      benefits: "Medium",
      compliance: "Low",
    },
    {
      business: "Sole Proprietorship",
      type: "Freelancers, Small Shops",
      benefits: "Low",
      compliance: "Minimal",
    },
  ];

  const steps = [
    {
      label: "Consultation",
      icon: "../assets/business/consult.png",
    },
    {
      label: "Document Preparation",
      icon: "../assets/business/document.png",
    },
    {
      label: "Filing & Compliance",
      icon: "../assets/business/filing.png",
    },
    {
      label: "Get Your Certificate",
      icon: "../assets/business/certificate.png",
    },
  ];

  const features = [
    "Expert CA Assistance",
    "Quick & Paperless Process",
    "Affordable & Transparent Pricing",
    "End-to-End Compliance Support",
  ];

  return (
    <>
      {/* Helmet */}
      <Helmet>
        <title>Business Registration Services - 360fintax</title>
        <meta
          name="description"
          content="Register your business seamlessly with 360fintax. Our expert team ensures a hassle-free process, guiding you through every step of business registration."
        />
        <link
          rel="canonical"
          href="https://tax-services-web.vercel.app/business-registration"
        />
      </Helmet>
      {/* Hero Section */}
      <Stack
        bgColor={secondBg}
        py="28"
        px={{ base: "5", md: "10", lg: "20" }}
        alignItems="center"
      >
        <RichText
          subtitle="Start Your Business with Ease"
          title="From idea to incorporation – we simplify your business registration process."
          w={{ base: "100%", lg: "70%" }}
          pt={{ base: "10", md: "10" }}
        />
        <AppointButton text={<SimpleOutlineButton title="Register Now" />} />
        {/* <Image src="../assets/about.png" h="330px" /> */}
      </Stack>

      {/* Why Business Registration Matters */}
      <Stack gap="14" py="10" px={{ base: "5", md: "10", lg: "20" }}>
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          textAlign={{ base: "center", md: "left" }}
          gap={{ base: "3", md: "0" }}
        >
          <Text
            fontSize={{ base: "xl", md: "3xl", lg: "4xl" }}
            fontWeight="bold"
            lineHeight="1"
          >
            Why Business Registration Matters?
          </Text>
          <Text
            fontSize={{ base: "xs", md: "lg", lg: "xl" }}
            textAlign={{ base: "center", md: "justify" }}
            w={{ base: "4xs", md: "md", lg: "2xl" }}
            color="gray.600"
          >
            Proper registration protects your business, builds trust, and
            ensures legal compliance. With 360fintax, you ensure compliance,
            gain tax benefits, and build credibility with customers and
            investors.
          </Text>
        </Grid>

        {/* Table */}
        <Table.Root
          data-aos="slide-up"
          size={{ base: "xs", md: "lg" }}
          variant="outline"
          fontSize={{ base: "2xs", md: "md" }}
        >
          <Table.Header bgColor={secondBg}>
            <Table.Row>
              <Table.ColumnHeader>Business Type</Table.ColumnHeader>
              <Table.ColumnHeader>Best For </Table.ColumnHeader>
              <Table.ColumnHeader>Tax Benefits </Table.ColumnHeader>
              <Table.ColumnHeader>Compliance Level</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tableData.map((data) => (
              <Table.Row key={data.id}>
                <Table.Cell>{data.business}</Table.Cell>
                <Table.Cell>{data.type}</Table.Cell>
                <Table.Cell>{data.benefits}</Table.Cell>
                <Table.Cell>{data.compliance}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        {/* CTA */}
        <Stack
          alignItems="center"
          textAlign="center"
          gap={5}
          data-aos="fade-up"
        >
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="semibold">
            Need Help Choosing? Talk to Our Experts
          </Text>
          <AppointButton text={<FilledButton title="Book a Call" />} />
        </Stack>
      </Stack>

      {/* Steps */}
      <Box py="10" px={{ base: "5", md: "5", lg: "20" }}>
        <Steps
          title="Our Fast & Easy Process"
          colorText="Fast & Easy"
          buttonText="File Now"
          steps={steps}
        />
      </Box>

      {/* WHY CHOOSE US */}
      <Stack
        alignItems="center"
        px={{ base: "5", md: "10", lg: "20" }}
        py={10}
        data-aos="fade-up"
      >
        <RichText
          subtitle="Business Registration"
          title="Why Choose Us?"
          colorText="Choose Us"
        />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          py={2}
          alignItems="center"
          gap={{ base: "10", md: "0" }}
        >
          <Stack gap={5}>
            {features.map((feature, index) => (
              <HStack gap={3}>
                <GiCheckMark />
                <Text fontSize="lg">{feature}</Text>
              </HStack>
            ))}
            <FilledButton
              title="File Your ITR Now"
              mx={{ base: "auto", md: "0" }}
            />
          </Stack>
          <Image src="../assets/business/business.png" h="400px" />
        </Grid>
      </Stack>
      {/* Testmonials */}
      <Stack
        data-aos="fade-up"
        textAlign="center"
        gap="10"
        py={{ base: "10", md: "10", lg: "10" }}
        alignItems="center"
      >
        <RichText
          title="Trusted by Clients,Proven Results"
          colorText="Proven Results"
          description="Discover how we've helped individuals and businesses stay compliant and stress-free, year after year."
          w={{ base: "xs", md: "3xl" }}
        />
        <TestimonialSwiper />
      </Stack>
    </>
  );
};

export default BusinessScreen;
